import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trash2, ShieldAlert, Check, X, Star } from "lucide-react";
import {
  useListingMedia, usePresignAndUpload, usePatchListingMedia, useDeleteListingMedia, type MediaRow,
} from "@/hooks/useMedia.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Badge } from "@/components/ui/badge.js";
import { DropZone } from "@/components/DropZone.js";
import { RoleGate } from "@/components/RoleGate.js";
import { I18nFieldTabs, type LocaleKey } from "@/components/signature/I18nFieldTabs.js";
import { ListingChrome } from "@/components/signature/ListingChrome.js";
import { cn } from "@/lib/cn.js";
import { env } from "@/env.js";

function publicUrl(storageKey: string): string {
  return `${env.VITE_SUPABASE_URL}/storage/v1/object/public/media-public/${storageKey}`;
}

function MediaTile({
  row, index, selected, onSelect, onDelete, deleting,
}: {
  row: MediaRow;
  index: number;
  selected: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  deleting: boolean;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const [confirming, setConfirming] = useState(false);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const isCover = index === 0;
  const flagged = row.asset.privacy_scan_status === "flagged";

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onSelect(row.id)}
      className={cn(
        "group relative aspect-square cursor-grab overflow-hidden rounded-lg border bg-surface-2 touch-none",
        selected ? "border-accent ring-2 ring-accent/40" : "border-rule",
        isDragging && "cursor-grabbing shadow-lg",
      )}
    >
      {row.asset.kind === "video" ? (
        <video src={publicUrl(row.asset.storage_key)} className="size-full object-cover" />
      ) : (
        <img src={publicUrl(row.asset.storage_key)} alt="" className="size-full object-cover" loading="lazy" draggable={false} />
      )}

      {/* order index / cover badge (top-left) */}
      {isCover ? (
        <Badge tone="accent" className="absolute left-1.5 top-1.5 gap-1 shadow">
          <Star className="size-3" /> Copertina
        </Badge>
      ) : (
        <span className="absolute left-1.5 top-1.5 grid size-6 place-items-center rounded-full bg-ink/70 text-meta font-mono text-white">
          {index + 1}
        </span>
      )}

      {/* privacy flag (bottom-left) */}
      {flagged ? (
        <Badge tone="risk" className="absolute bottom-1.5 left-1.5 gap-1 shadow">
          <ShieldAlert className="size-3" /> privacy
        </Badge>
      ) : null}

      {/* delete (top-right) — confirm inline; stop propagation so it never starts a drag/select */}
      <RoleGate min="editor">
        {confirming ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/80 p-2 text-center"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <p className="text-meta font-medium text-white">Eliminare definitivamente?</p>
            <div className="flex gap-2">
              <Button size="sm" variant="danger" disabled={deleting}
                onClick={() => { onDelete(row.id); }}>
                <Check className="size-4" /> Sì
              </Button>
              <Button size="sm" variant="secondary" disabled={deleting}
                onClick={() => setConfirming(false)}>
                <X className="size-4" /> No
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            aria-label="Elimina foto"
            className="absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-full bg-ink/70 text-white opacity-0 transition-opacity hover:bg-risk group-hover:opacity-100 focus:opacity-100"
            onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </RoleGate>
    </div>
  );
}

export default function ListingMedia(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeLoc, setActiveLoc] = useState<LocaleKey>("it");
  if (!id) return <p className="text-risk">id mancante</p>;

  const { data: media = [], isLoading } = useListingMedia(id);
  const upload = usePresignAndUpload(id);
  const patchMedia = usePatchListingMedia(id);
  const deleteMedia = useDeleteListingMedia(id);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const selected = media.find((m) => m.id === selectedId) ?? null;

  async function handleFiles(files: File[]) {
    for (const f of files) {
      try {
        await upload.mutateAsync(f);
        toast.success(`Caricato: ${f.name}`);
      } catch (e) {
        toast.error(`${f.name}: ${(e as Error).message}`);
      }
    }
  }

  async function handleDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIndex = media.findIndex((m) => m.id === e.active.id);
    const newIndex = media.findIndex((m) => m.id === e.over!.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(media, oldIndex, newIndex);
    await Promise.all(
      reordered.map((m, idx) =>
        m.position === idx ? null : patchMedia.mutateAsync({ id: m.id, patch: { position: idx } }),
      ),
    );
  }

  async function handleDelete(rowId: string) {
    try {
      await deleteMedia.mutateAsync(rowId);
      if (selectedId === rowId) setSelectedId(null);
      toast.success("Foto eliminata");
    } catch (e) {
      toast.error(`Eliminazione fallita: ${(e as Error).message}`);
    }
  }

  async function handleAltChange(rowId: string, alt: Record<string, string>) {
    await patchMedia.mutateAsync({ id: rowId, patch: { alt } });
  }

  return (
    <div className="space-y-6">
      <ListingChrome id={id} />
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="mt-1 text-h1 font-display text-ink">Media</h1>
          <p className="text-body text-ink-soft">
            {media.length} file · <span className="text-ink-mute">la prima foto è la copertina · trascina per riordinare · le modifiche vanno online subito</span>
          </p>
        </div>
      </header>

      <RoleGate min="editor">
        <DropZone onFiles={handleFiles} disabled={upload.isPending} />
      </RoleGate>

      {isLoading ? (
        <p className="text-ink-mute font-mono text-meta uppercase">carico...</p>
      ) : media.length === 0 ? (
        <p className="rounded border border-rule bg-surface p-6 text-center text-ink-mute">
          Nessun media caricato.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <SortableContext items={media.map((m) => m.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {media.map((row, i) => (
                <MediaTile
                  key={row.id}
                  row={row}
                  index={i}
                  selected={selectedId === row.id}
                  onSelect={setSelectedId}
                  onDelete={handleDelete}
                  deleting={deleteMedia.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Alt-text editor for the selected photo (i18n). */}
      {selected ? (
        <div className="flex items-start gap-4 rounded-lg border border-rule bg-surface p-4">
          <img src={publicUrl(selected.asset.storage_key)} alt="" className="size-20 shrink-0 rounded object-cover" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-mono text-meta text-ink-mute truncate">{selected.asset.storage_key}</p>
              <Button size="sm" variant="ghost" onClick={() => setSelectedId(null)}>Chiudi</Button>
            </div>
            <I18nFieldTabs
              value={activeLoc}
              onValueChange={setActiveLoc}
              render={(loc) => {
                const altMap = (selected.alt ?? {}) as Record<string, string>;
                return (
                  <Input
                    key={`${selected.id}-${loc}`}
                    defaultValue={altMap[loc] ?? ""}
                    placeholder={`Testo alternativo · ${loc.toUpperCase()}`}
                    onBlur={(e) => handleAltChange(selected.id, { ...altMap, [loc]: e.target.value })}
                  />
                );
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
